import { Controller } from "@hotwired/stimulus"

// The course reader rail's Table of Contents search (TMS-1828).
//
// Filters the rendered contents in place by title — lessons, assessments, the
// start screen, and the active lesson's format jump-links, block entries and
// their sections. Nothing is re-rendered, so completion ticks, the current-lesson
// highlight and every link keep working on whatever stays visible.
//
// Structure is kept: a matching section keeps its entry and lesson visible above
// it, and a matching lesson keeps all of its children. Items are hidden with a
// class rather than [hidden] because academy-lesson already toggles [hidden] on
// the format jump-links and lesson-completion on the ticks.
const HIDDEN = "is-search-hidden"
// Marks the entries this controller expanded, so clearing collapses only those.
const EXPANDED = "searchExpanded"

export default class extends Controller {
  static targets = ["toggle", "field", "input", "list", "empty", "status"]

  disconnect() {
    this.reset()
  }

  toggle() {
    if (this.fieldTarget.hidden) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.fieldTarget.hidden = false
    this.toggleTarget.setAttribute("aria-expanded", "true")
    this.inputTarget.focus()
  }

  close() {
    this.inputTarget.value = ""
    this.reset()
    this.fieldTarget.hidden = true
    this.toggleTarget.setAttribute("aria-expanded", "false")
    this.toggleTarget.focus()
  }

  filter() {
    const term = normalize(this.inputTarget.value)
    if (!term) {
      this.reset()
      return
    }

    const matches = (el) => !!el && normalize(el.textContent).includes(term)
    let visibleCount = 0

    this.topLevelItems.forEach((item) => {
      const title = item.querySelector(".academy-toc-lesson-title, .academy-syllabus-item-title")
      const titleMatch = matches(title)
      let childMatch = false

      // Format jump-links and block entries only exist under the active lesson.
      item.querySelectorAll(".academy-toc-lesson-sections > .academy-toc-section").forEach((section) => {
        const show = titleMatch || matches(section)
        setVisible(section, show)
        childMatch ||= show && !titleMatch
      })

      item.querySelectorAll(".academy-toc-entry").forEach((entry) => {
        const labelMatch = titleMatch || matches(entry.querySelector(".academy-toc-entry-label"))
        let sectionMatch = false

        entry.querySelectorAll(".academy-toc-sections > .academy-toc-section").forEach((section) => {
          const show = labelMatch || matches(section)
          setVisible(section, show)
          sectionMatch ||= show && !labelMatch
        })

        setVisible(entry, labelMatch || sectionMatch)
        // A hit inside a collapsed entry would otherwise stay out of sight.
        if (sectionMatch) this.expand(entry)
        childMatch ||= (labelMatch || sectionMatch) && !titleMatch
      })

      const show = titleMatch || childMatch
      setVisible(item, show)
      if (show) visibleCount += 1
    })

    const empty = visibleCount === 0
    this.listTarget.hidden = empty
    this.emptyTarget.hidden = !empty
    this.emptyTarget.textContent = empty ? `No results for “${this.inputTarget.value.trim()}”` : ""
    this.statusTarget.textContent = empty
      ? "No results"
      : `${visibleCount} ${visibleCount === 1 ? "result" : "results"}`
  }

  // Restores the full contents exactly as the server rendered it.
  reset() {
    this.element.querySelectorAll(`.${HIDDEN}`).forEach((el) => el.classList.remove(HIDDEN))
    this.element.querySelectorAll(`[data-${dasherize(EXPANDED)}]`).forEach((entry) => {
      delete entry.dataset[EXPANDED]
      entry.classList.add("is-collapsed")
      entry.querySelector(".academy-toc-entry-toggle")?.setAttribute("aria-expanded", "false")
    })
    if (this.hasListTarget) this.listTarget.hidden = false
    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = true
      this.emptyTarget.textContent = ""
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  expand(entry) {
    if (!entry.classList.contains("is-collapsed")) return

    entry.classList.remove("is-collapsed")
    entry.dataset[EXPANDED] = "true"
    entry.querySelector(".academy-toc-entry-toggle")?.setAttribute("aria-expanded", "true")
  }

  get topLevelItems() {
    return Array.from(this.listTarget.children).filter((el) => el.tagName === "LI")
  }
}

function setVisible(el, visible) {
  el.classList.toggle(HIDDEN, !visible)
}

// Case- and whitespace-insensitive, so "horm" finds "Hormone Therapy".
function normalize(text) {
  return (text || "").replace(/\s+/g, " ").trim().toLowerCase()
}

function dasherize(key) {
  return key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)
}
